/**
 * Dashboard Page with Customizable Widgets
 */

import React, { useState } from 'react';
import {
  Row,
  Col,
  Typography,
  Button,
  Spin,
  Empty,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { instancesApi } from '@api/index';
import { dashboardWidgetsApi, type DashboardWidget as Widget, type WidgetPreset, type CreateWidgetInput, type UpdateWidgetInput } from '@/api/dashboard-widgets';
import { DashboardWidget, WidgetConfigDrawer, WidgetPresetsModal } from '@/components/dashboard';

const { Text } = Typography;

export default function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI State
  const [presetsModalOpen, setPresetsModalOpen] = useState(false);
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);

  // Fetch summary stats (keeping for the top row)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const [myTasks, overdueCount] = await Promise.all([
        instancesApi.getMyTasks(false),
        instancesApi.getOverdueCount(),
      ]);

      const pendingTasks = myTasks.filter((t) => t.status === 'pending');
      const inProgressTasks = myTasks.filter((t) => t.status === 'in_progress');
      const completedToday = myTasks.filter(
        (t) =>
          t.status === 'completed' &&
          t.completedAt &&
          new Date(t.completedAt).toDateString() === new Date().toDateString()
      );

      return {
        pending: pendingTasks.length,
        inProgress: inProgressTasks.length,
        completedToday: completedToday.length,
        overdue: overdueCount,
      };
    },
  });

  // Fetch user's widgets
  const { data: widgets, isLoading: widgetsLoading } = useQuery({
    queryKey: ['dashboard-widgets'],
    queryFn: () => dashboardWidgetsApi.list(),
  });

  // Create widget mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateWidgetInput) => dashboardWidgetsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-widgets'] });
      message.success('Widget added');
    },
    onError: () => {
      message.error('Failed to create widget');
    },
  });

  // Create from preset mutation
  const createFromPresetMutation = useMutation({
    mutationFn: (presetId: string) => dashboardWidgetsApi.createFromPreset(presetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-widgets'] });
      message.success('Widget added');
      setPresetsModalOpen(false);
    },
    onError: () => {
      message.error('Failed to create widget');
    },
  });

  // Update widget mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWidgetInput }) =>
      dashboardWidgetsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-widgets'] });
      message.success('Widget updated');
    },
    onError: () => {
      message.error('Failed to update widget');
    },
  });

  // Delete widget mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => dashboardWidgetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-widgets'] });
      message.success('Widget removed');
    },
    onError: () => {
      message.error('Failed to delete widget');
    },
  });

  // Handlers
  const handleAddWidget = () => {
    setPresetsModalOpen(true);
  };

  const handleSelectPreset = (preset: WidgetPreset) => {
    createFromPresetMutation.mutate(preset.id);
  };

  const handleCustomWidget = () => {
    setPresetsModalOpen(false);
    setEditingWidget(null);
    setConfigDrawerOpen(true);
  };

  const handleEditWidget = (widget: Widget) => {
    setEditingWidget(widget);
    setConfigDrawerOpen(true);
  };

  const handleDeleteWidget = (widget: Widget) => {
    deleteMutation.mutate(widget.id);
  };

  const handleSaveWidget = async (data: CreateWidgetInput | UpdateWidgetInput) => {
    if (editingWidget) {
      await updateMutation.mutateAsync({ id: editingWidget.id, data });
    } else {
      await createMutation.mutateAsync(data as CreateWidgetInput);
    }
    setConfigDrawerOpen(false);
    setEditingWidget(null);
  };

  // Calculate grid columns based on widget width
  const getColSpan = (width: number) => {
    switch (width) {
      case 1:
        return 6; // 1/4 of row
      case 2:
        return 12; // 1/2 of row
      case 3:
        return 18; // 3/4 of row
      case 4:
        return 24; // full row
      default:
        return 12;
    }
  };

  if (statsLoading || widgetsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const hasWidgets = widgets && widgets.length > 0;

  // Blueprint stat card style
  const statCardStyle: React.CSSProperties = {
    background: 'var(--surface-1)',
    border: '1px solid var(--border-default)',
    borderRadius: 2,
    boxShadow: 'none',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <div>
      {/* Summary Stats Row - Always visible */}
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} lg={6}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/tasks?status=pending')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/tasks?status=pending')}
            style={{ ...statCardStyle }}
          >
            {/* Top accent tick */}
            <div style={{ height: 2, background: 'var(--status-info)', borderRadius: 0 }} />
            <div style={{ padding: '14px 16px 12px' }}>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-tertiary)',
                marginBottom: 8,
              }}>
                PENDING TASKS
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ClockCircleOutlined style={{ color: 'var(--status-info)', fontSize: 16 }} />
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 36,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: 'var(--text-primary)',
                }}>
                  {stats?.pending || 0}
                </span>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/tasks?status=in_progress')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/tasks?status=in_progress')}
            style={{ ...statCardStyle }}
          >
            <div style={{ height: 2, background: 'var(--status-warning)', borderRadius: 0 }} />
            <div style={{ padding: '14px 16px 12px' }}>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-tertiary)',
                marginBottom: 8,
              }}>
                IN PROGRESS
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ClockCircleOutlined style={{ color: 'var(--status-warning)', fontSize: 16 }} />
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 36,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: 'var(--text-primary)',
                }}>
                  {stats?.inProgress || 0}
                </span>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div style={{ ...statCardStyle, cursor: 'default' }}>
            <div style={{ height: 2, background: 'var(--status-success)', borderRadius: 0 }} />
            <div style={{ padding: '14px 16px 12px' }}>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-tertiary)',
                marginBottom: 8,
              }}>
                COMPLETED TODAY
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircleOutlined style={{ color: 'var(--status-success)', fontSize: 16 }} />
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 36,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: 'var(--text-primary)',
                }}>
                  {stats?.completedToday || 0}
                </span>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/tasks?status=delayed')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/tasks?status=delayed')}
            style={{ ...statCardStyle }}
          >
            <div style={{ height: 2, background: (stats?.overdue || 0) > 0 ? 'var(--status-error)' : 'var(--border-subtle)', borderRadius: 0 }} />
            <div style={{ padding: '14px 16px 12px' }}>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-tertiary)',
                marginBottom: 8,
              }}>
                OVERDUE
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ExclamationCircleOutlined style={{ color: (stats?.overdue || 0) > 0 ? 'var(--status-error)' : 'var(--text-tertiary)', fontSize: 16 }} />
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 36,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: (stats?.overdue || 0) > 0 ? 'var(--status-error)' : 'var(--text-primary)',
                }}>
                  {stats?.overdue || 0}
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Custom Widgets Grid */}
      <div style={{ marginTop: 24 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-secondary)',
          }}>
            MY DASHBOARD
          </span>
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddWidget}
            style={{
              borderRadius: 2,
              border: '1px solid var(--border-default)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Add Widget
          </Button>
        </div>

        {!hasWidgets ? (
          <div style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border-default)',
            borderRadius: 2,
          }}>
            <Empty
              description={
                <span>
                  No custom widgets yet.
                  <br />
                  <Text type="secondary">
                    Add widgets to customize your dashboard and track what matters to you.
                  </Text>
                </span>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddWidget}
                style={{ borderRadius: 2, background: 'var(--accent-ink)', borderColor: 'var(--accent-ink)' }}
              >
                Add Your First Widget
              </Button>
            </Empty>
          </div>
        ) : (
          <Row gutter={[12, 12]}>
            {widgets?.map((widget) => (
              <Col key={widget.id} span={getColSpan(widget.grid_width)}>
                <DashboardWidget
                  widget={widget}
                  onEdit={handleEditWidget}
                  onDelete={handleDeleteWidget}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Modals & Drawers */}
      <WidgetPresetsModal
        open={presetsModalOpen}
        onClose={() => setPresetsModalOpen(false)}
        onSelect={handleSelectPreset}
        onCustom={handleCustomWidget}
      />

      <WidgetConfigDrawer
        open={configDrawerOpen}
        widget={editingWidget}
        onClose={() => {
          setConfigDrawerOpen(false);
          setEditingWidget(null);
        }}
        onSave={handleSaveWidget}
      />
    </div>
  );
}
